import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import removeEmptyValuesFromObject from '~publish/legacy/utils/removeEmptyValuesFromObject'

import type { RetweetMedia } from '../../../composer/composer/entities/factories'

// The extension sends the reposted tweet's text in X's legacy `RT @handle: …`
// form. That prefix is transport, not content: the preview renders
// `retweet.text` as the reposted tweet's body, so leaving it in shows the user
// "RT @handle: hello" where X shows "hello". Handles are ≤15 word characters.
const LEGACY_RETWEET_PREFIX = /^RT @\w{1,15}:\s*/

function stripLegacyRetweetPrefix(text: string | null): string | null {
  return text?.replace(LEGACY_RETWEET_PREFIX, '') ?? null
}

/**
 * Constructs post data from the query string used by the browser extension.
 * @param queryParams - Query string from the extension.
 */
function formatPostFromQuery(queryParams: string): PrefillPostData {
  if (!queryParams) return {}

  // Note: via and title are fields that were available with button.buffer.com.
  // We added logic here to support pulling them in when we started redirecting
  // buffer.com/add to publish.buffer.com/compose in order to retain functionality.
  let text = ''

  const query = new URLSearchParams(queryParams)
  const textParam = query.get('text')
  const titleParam = query.get('title')
  if (textParam) {
    text = textParam
  }
  if (titleParam) {
    text = titleParam
  }

  const url = query.get('url')
  if (url) {
    text = text === '' ? url : `${text} ${url}`
  }

  let postWithNullValues: PrefillPostData = { text }

  const picture = query.get('picture')
  if (picture) {
    postWithNullValues = {
      ...postWithNullValues,
      imageSrc: picture,
      // The extension still sends the legacy media shape used by this flow.
      // @ts-expect-error -- The legacy picture payload lacks current media metadata.
      media: {
        picture,
        photo: picture,
        thumbnail: picture,
      },
    }
  }

  const via = query.get('via')
  if (via) {
    postWithNullValues = {
      ...postWithNullValues,
      text: `${postWithNullValues.text} via @${via}`,
    }
  }

  const tweetId = query.get('retweeted_tweet_id')
  if (tweetId) {
    // The extension still sends the retweet prefix as post text. Keep it out
    // of the composer until the extension stops sending that legacy value.
    if (text.startsWith('RT ')) {
      postWithNullValues.text = ''
    }
    const username = query.get('retweeted_user_name')

    // Parse media data if present
    let media: RetweetMedia[] = []
    const mediaParam = query.get('retweeted_tweet_media')
    if (mediaParam) {
      try {
        const decodedMedia = atob(mediaParam)
        // The extension owns this JSON shape. Validation would reject payloads
        // that the legacy composer currently accepts.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Preserve the extension's legacy media payload.
        media = JSON.parse(decodedMedia)
      } catch {
        // do nothing
      }
    }

    const retweet = {
      user_id: query.get('retweeted_user_id'),
      username,
      profile_name: query.get('retweeted_user_display_name'),
      tweet_id: tweetId,
      url: `https://twitter.com/${username}/status/${tweetId}`,
      avatars: {
        http: query.get('retweeted_user_avatar'),
        https: query.get('retweeted_user_avatar'),
      },
      text: stripLegacyRetweetPrefix(query.get('text')),
      media,
    }

    postWithNullValues = {
      ...postWithNullValues,
      retweeted_tweet_id: tweetId,
      // The extension's retweet payload uses nullable legacy fields.
      // @ts-expect-error -- Preserve the nullable payload accepted by the legacy composer.
      retweet,
    }
  }

  return removeEmptyValuesFromObject(postWithNullValues)
}

export default formatPostFromQuery
